/**
 * Manning-stats filter — officer selection + time-period, in one popover.
 *
 * Single source of this UI: HomeTab renders the exact same <StatsFilterPopover>
 * in all three spots the stats appear (stacked layout, side-by-side layout, and
 * the docked bar), rather than three copies of a filter control. Whatever it's
 * set to drives both stats displays (the raw text under "Copy Stats" and the
 * hourly numbers atop the counter graph) from the SAME filtered text, so the two
 * never show different numbers for the same moment.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { naturalSortOfficers } from '@/config/modeConfig';
import type { OfficerMetadata } from '@/types/api';
import { EMPTY_STATS_FILTER, isStatsFilterActive, type StatsFilterValue } from '@/utils/statsFilter';
import { useOfficerOrder } from '@/utils/officerOrder';
import { groupOfficersByType, officerTypeOf, OFFICER_TYPE_PILL } from '@/utils/officerGrouping';
import { UI_SCALE } from '@/utils/uiScale';
import { useDragOffset } from '@/utils/useDragOffset';
import { DragBar } from '@/components/DragBar';

interface Props {
  officerSchedules: Record<string, number[]>;
  officerMetadata: Record<string, OfficerMetadata>;
  /** Slot → "HHMM", for the from/to time pickers. */
  timeLabels: string[];
  value: StatsFilterValue;
  onChange: (next: StatsFilterValue) => void;
  /** HHMM of now rounded down to the half hour (null outside the shift) — the default "from" time. */
  nowStart?: string | null;
  /** True while the "from" time is the automatic now-default rather than the user's pick. */
  startIsNow?: boolean;
  /** Go back to following the clock for the "from" time. */
  onUseNow?: () => void;
}

export function StatsFilterPopover({ officerSchedules, officerMetadata, timeLabels, value, onChange, nowStart, startIsNow, onUseNow }: Props) {
  const [open, setOpen] = useState(false);
  const drag = useDragOffset();
  useEffect(() => { if (!open) drag.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  // The panel is portalled to document.body and positioned in viewport
  // coordinates (position: fixed) rather than nested inside this button's own
  // stacking context (position: absolute) — one of this popover's three call
  // sites renders it inside a scrollable panel (the expanded Stats drawer),
  // and an absolutely-positioned child there gets clipped by that ancestor's
  // own overflow:auto instead of floating above everything like it's meant to.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const PANEL_H = 400;
  const PANEL_W = 312;

  // Click-outside-to-close via a document listener rather than a full-screen
  // fixed backdrop — that backdrop used to sit on top of the counter/officer
  // canvases while open and swallow every wheel/touch event over them, which
  // read as the graphs having frozen. A listener closes on an outside click
  // without ever intercepting a scroll. containerRef no longer wraps the
  // (now portalled) panel, so its own pointerdown is checked separately.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) { setPanelPos(null); return; }
    const place = () => {
      const r = btnRef.current!.getBoundingClientRect();
      const openUp = r.top > PANEL_H * UI_SCALE + 8 || r.top > window.innerHeight - r.bottom;
      setPanelPos({
        top: openUp ? r.top - 4 : r.bottom + 4,
        left: Math.min(Math.max(4, r.left), window.innerWidth - PANEL_W * UI_SCALE - 4),
        openUp,
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const officerOrder = useOfficerOrder();   // re-sort when the user drags rows
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allKeys = useMemo(() => naturalSortOfficers(Object.keys(officerSchedules)), [officerSchedules, officerOrder]);
  // Grouped by officer type in the SAME order the officer graph lists officers
  // in — one shared source of truth (groupOfficersByType) instead of a second,
  // independently-chosen MAIN/SOS/OT ordering.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matching = allKeys.filter(id =>
      !q || id.toLowerCase().includes(q) || (officerMetadata[id]?.name ?? '').toLowerCase().includes(q));
    return groupOfficersByType(matching, id => id, id => officerTypeOf(id, officerMetadata[id]?.officer_type))
      .map(({ type, items }) => ({ type, officers: items }));
  }, [allKeys, officerMetadata, search]);

  const selected = value.officerKeys ?? new Set(allKeys);
  const hiddenCount = value.officerKeys === null ? 0 : Math.max(0, allKeys.length - value.officerKeys.size);
  const active = isStatsFilterActive(value, allKeys.length);

  const toggleOfficer = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    // A full set is the same as "no filter" — keep the null/unfiltered state canonical
    // rather than an explicit set that happens to contain everyone.
    onChange({ ...value, officerKeys: next.size === allKeys.length ? null : next });
  };
  const toggleGroup = (ids: string[]) => {
    const allOn = ids.every(id => selected.has(id));
    const next = new Set(selected);
    if (allOn) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    onChange({ ...value, officerKeys: next.size === allKeys.length ? null : next });
  };
  const clearAll = () => onChange(EMPTY_STATS_FILTER);

  return (
    <div ref={containerRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        title="Filter which officers and time period the stats count"
        style={{
          display: 'flex', alignItems: 'center', gap: 4, fontSize: '10px', fontWeight: 700,
          padding: '4px 7px', borderRadius: '4px', cursor: 'pointer',
          border: `1px solid ${active ? '#6366f1' : '#c7d2fe'}`,
          color: active ? '#312e81' : '#4338ca',
          background: active ? '#e0e7ff' : '#eef2ff',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
          <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6l-6.3 8.4V19a1 1 0 0 1-1.45.9l-4-2A1 1 0 0 1 9 17v-4L2.7 4.6A1 1 0 0 1 3 4z" />
        </svg>
        Filter{value.start ? ` · from ${value.start}${startIsNow ? ' (now)' : ''}` : ''}{value.end ? ` · to ${value.end}` : ''}{active && hiddenCount > 0 ? ` · ${hiddenCount} hidden` : ''}
      </button>

      {open && panelPos && createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              // zoom scales offsets as well as sizes, so they are given in zoomed units
              top: panelPos.openUp ? undefined : panelPos.top / UI_SCALE,
              bottom: panelPos.openUp ? (window.innerHeight - panelPos.top) / UI_SCALE : undefined,
              left: panelPos.left / UI_SCALE,
              zoom: UI_SCALE,
              zIndex: 9500,
              width: PANEL_W, maxHeight: PANEL_H, display: 'flex', flexDirection: 'column',
              background: 'white', borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
              border: '1px solid #e2e8f0', overflow: 'hidden',
              transform: `translate(${drag.offset.dx / UI_SCALE}px, ${drag.offset.dy / UI_SCALE}px)`, // zoom scales it
            }}
          >
            <DragBar drag={drag} rounded={false} />
            {/* Search on top, time window on its own row beneath. The time pickers
                are filled indigo selects (not white boxes on a white panel) so
                they read as controls, and every time — including the unset
                defaults, which show the shift's first/last slot — is plain HHMM
                like everywhere else in the app. */}
            <div style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search officer…"
                  style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', fontSize: 14, padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                />
                {active && (
                  <button
                    onClick={clearAll}
                    title="Clear the filter"
                    style={{ flexShrink: 0, background: 'none', border: 'none', color: '#4f46e5', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 2px' }}
                  >
                    Reset
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
                  <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
                </svg>
                {(['start', 'end'] as const).map((k, i) => (
                  <Fragment key={k}>
                    {i === 1 && <span style={{ color: '#6366f1', fontWeight: 800, fontSize: 14 }}>–</span>}
                    <select
                      value={value[k] ?? ''}
                      onChange={e => onChange({ ...value, [k]: e.target.value || null })}
                      title={k === 'start' ? 'Count from this time' : 'Count up to this time'}
                      aria-label={k === 'start' ? 'Count from this time' : 'Count up to this time'}
                      style={{
                        flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, fontFamily: 'monospace', padding: '5px 4px', borderRadius: 6, cursor: 'pointer',
                        border: `1.5px solid ${value[k] ? '#4f46e5' : '#a5b4fc'}`,
                        background: value[k] ? '#e0e7ff' : '#eef2ff',
                        color: value[k] ? '#312e81' : '#4338ca',
                        boxShadow: '0 1px 2px rgba(55,48,163,0.18)',
                      }}
                    >
                      <option value="">{(k === 'start' ? timeLabels[0] : timeLabels[timeLabels.length - 1]) ?? ''}</option>
                      {(value[k] && !timeLabels.includes(value[k]!) ? [...timeLabels, value[k]!].sort() : timeLabels).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Fragment>
                ))}
              </div>
              {(value.start || value.end || nowStart) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4338ca', background: '#eef2ff', borderRadius: 6, padding: '4px 8px' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {value.start || value.end
                      ? `Stats filtered: ${value.start ?? 'shift start'} → ${value.end ?? 'shift end'}${startIsNow ? ' (from now)' : ''}`
                      : 'Showing the full shift'}
                  </span>
                  {(value.start || value.end) && (
                    <button
                      onClick={() => onChange({ ...value, start: null, end: null })}
                      style={{ flexShrink: 0, background: '#4f46e5', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, padding: '3px 8px', cursor: 'pointer' }}
                    >Full shift</button>
                  )}
                  {!value.start && !value.end && nowStart && onUseNow && (
                    <button
                      onClick={onUseNow}
                      style={{ flexShrink: 0, background: '#4f46e5', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, padding: '3px 8px', cursor: 'pointer' }}
                    >From {nowStart}</button>
                  )}
                </div>
              )}
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '3px 6px 6px' }}>
              {groups.map(g => {
                const pill = OFFICER_TYPE_PILL[g.type];
                const allOn = g.officers.every(id => selected.has(id));
                return (
                  <div key={g.type} style={{ marginBottom: 4 }}>
                    <div
                      onClick={() => toggleGroup(g.officers)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 4px', cursor: 'pointer' }}
                    >
                      <input type="checkbox" checked={allOn} onClick={e => e.stopPropagation()} onChange={() => toggleGroup(g.officers)} style={{ width: 15, height: 15, accentColor: '#6366f1' }} />
                      <span style={{ fontSize: 12, fontWeight: 800, padding: '1px 8px', borderRadius: 99, background: pill.bg, color: pill.fg }}>{g.type}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{g.officers.length}</span>
                    </div>
                    {g.officers.map(id => (
                      <div
                        key={id}
                        onClick={() => toggleOfficer(id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 26px', cursor: 'pointer' }}
                      >
                        <input type="checkbox" checked={selected.has(id)} onClick={e => e.stopPropagation()} onChange={() => toggleOfficer(id)} style={{ width: 15, height: 15, accentColor: '#6366f1' }} />
                        <span style={{ fontFamily: 'monospace', fontSize: 14, color: '#334155' }}>{id}</span>
                        {officerMetadata[id]?.name && <span style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{officerMetadata[id].name}</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
              {groups.length === 0 && (
                <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '10px 0' }}>No officers match</div>
              )}
            </div>
          </div>,
          document.body
      )}
    </div>
  );
}
